// Shared helpers for the messaging composers (message_form.vue on web and
// app_embed_message_composer.vue in the native webview), so their behavior
// can't drift apart.

// One debounce for draft autosaves everywhere. Autosaves are idempotent
// create-by-token upserts (see MessagesController#create), so a fast cadence is
// safe — repeated saves converge on the same row.
export const AUTOSAVE_DEBOUNCE_MS = 1000;

// A v4 UUID for the client_token idempotency key. Uses crypto.randomUUID
// where available (modern Chrome/Firefox/Safari/Edge), falls back to
// crypto.getRandomValues (all other current browsers), and only as a last
// resort a non-crypto id — so it works across every browser we support.
export function generateClientToken() {
  try {
    const c = window.crypto || window.msCrypto;
    if (c && typeof c.randomUUID === 'function') return c.randomUUID();
    if (c && c.getRandomValues) {
      const b = new Uint8Array(16);
      c.getRandomValues(b);
      /* eslint-disable no-bitwise */
      b[6] = (b[6] & 0x0f) | 0x40; // version 4
      b[8] = (b[8] & 0x3f) | 0x80; // variant 10
      /* eslint-enable no-bitwise */
      const hex = Array.from(b, (x) => x.toString(16).padStart(2, '0')).join('');
      return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
    }
  } catch (e) {
    // fall through to the non-crypto fallback
  }
  return `cid-${Date.now()}-${Math.floor(Math.random() * 1e9)}`;
}

// Trix serializes a trailing newline as an empty block (and pads with <br>);
// strip those so an "empty" editor round-trips to blank and content comparisons
// don't see phantom whitespace.
export function trimTrailingEmptyBlocks(html) {
  if (!html) return '';
  let out = html;
  const emptyBlockRe = /(<(?:div|p)[^>]*>\s*(?:<br\s*\/?>)?\s*<\/(?:div|p)>\s*)+$/i;
  const trailingBrRe = /(<br\s*\/?>\s*)+$/i;
  let prev;
  do {
    prev = out;
    out = out.replace(emptyBlockRe, '').replace(trailingBrRe, '');
  } while (out !== prev);
  return out.trim();
}

// Reads the Trix editor's serialized HTML from its hidden input, trimmed of
// trailing empty blocks.
export function readComposerHtml(inputId) {
  const input = document.getElementById(inputId);
  return trimTrailingEmptyBlocks(input ? input.value : '');
}
